import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useParams } from "react-router-dom";
import AdminPageLayout from "../../../components/midpoint/admin/layout/AdminPageLayout";
import AdminPageHeader from "../../../components/midpoint/admin/layout/AdminPageHeader";
import {
  FieldMappingForm,
  ImportPreviewTable,
  ImportStatusBadge,
  ImportStepIndicator,
  ImportSummaryCards,
} from "../../../components/dealer/InventoryImportShared";
import {
  IMPORT_MODES,
  PUBLISH_MODES,
  TEMPLATE_FIELDS,
  importStepFromStatus,
} from "../../../constants/automapInventoryImportConstants";
import {
  adminImportErrorsUrl,
  executeAdminImport,
  fetchAdminImport,
  trackImportEvent,
  updateAdminImportMapping,
  updateAdminImportRows,
} from "../../../services/automapInventoryImportApi";

export default function AdminInventoryImportDetailPage() {
  const { importId } = useParams();
  const [importRecord, setImportRecord] = useState(null);
  const [headers, setHeaders] = useState([]);
  const [mapping, setMapping] = useState({});
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchAdminImport(importId);
      setImportRecord(data.import);
      setMapping(data.import.fieldMapping || {});
      const firstRow = data.import.rows?.[0];
      if (firstRow?.rawData) setHeaders(Object.keys(firstRow.rawData));
    } catch (err) {
      setError(err.message || "Importación no encontrada.");
    } finally {
      setLoading(false);
    }
  }, [importId]);

  useEffect(() => {
    load();
  }, [load]);

  const currentStep = importStepFromStatus(importRecord);
  const sampleRow = importRecord?.rows?.[0]?.rawData;

  const saveMapping = async () => {
    setBusy(true);
    try {
      const data = await updateAdminImportMapping(importId, mapping);
      setImportRecord(data.import);
    } catch (err) {
      setError(err.message || "Error al guardar mapeo.");
    } finally {
      setBusy(false);
    }
  };

  const handleRowAction = (rowId, action) => {
    setImportRecord((prev) => ({
      ...prev,
      rows: prev.rows.map((row) => (row.id === rowId ? { ...row, action } : row)),
    }));
  };

  const saveRowActions = async () => {
    const rows = importRecord.rows.map((row) => ({ rowId: row.id, action: row.action }));
    const data = await updateAdminImportRows(importId, rows);
    setImportRecord(data.import);
  };

  const runImport = async () => {
    setBusy(true);
    setError("");
    try {
      await saveRowActions();
      trackImportEvent("inventory_import_started", { importId, admin: true });
      const data = await executeAdminImport(importId);
      setImportRecord(data.import);
    } catch (err) {
      setError(err.message || "Error al importar.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <AdminPageLayout>
      <AdminPageHeader
        eyebrow="AutoMapa · Importación"
        title={importRecord?.sourceFilename || "Detalle de importación"}
        description="Revisión operativa de carga masiva."
      />
      <main className="admin-console__main wrap import-page">
        {loading ? <p className="dealer-state-box">Cargando…</p> : null}
        {!loading && importRecord ? (
          <>
            <ImportStepIndicator currentStep={currentStep} />
            <div className="import-page-header">
              <ImportStatusBadge status={importRecord.status} />
              <a className="dealer-portal-btn" href={adminImportErrorsUrl(importId)}>
                Errores CSV
              </a>
              <Link className="dealer-portal-btn" to="/admin/inventory/imports">
                Volver
              </Link>
            </div>
            <ImportSummaryCards importRecord={importRecord} />
            <div className="dealer-state-box">
              <p>Dealer ID: {importRecord.dealerId}</p>
              <p>
                Modo: {IMPORT_MODES.find((m) => m.value === importRecord.importMode)?.label} · Publicación:{" "}
                {PUBLISH_MODES.find((m) => m.value === importRecord.publishMode)?.label}
              </p>
            </div>
            {error ? <p className="import-error">{error}</p> : null}

            {importRecord.status === "mapping_required" ? (
              <section className="dealer-state-box">
                <FieldMappingForm
                  fields={TEMPLATE_FIELDS}
                  headers={headers}
                  mapping={mapping}
                  sampleRow={sampleRow}
                  onChange={(key, value) => setMapping((prev) => ({ ...prev, [key]: value }))}
                />
                <button type="button" className="dealer-portal-btn primary" disabled={busy} onClick={saveMapping}>
                  Generar vista previa
                </button>
              </section>
            ) : null}

            {["preview_ready", "completed", "completed_with_errors"].includes(importRecord.status) ? (
              <>
                <ImportPreviewTable
                  rows={importRecord.rows}
                  editable={importRecord.status === "preview_ready"}
                  onActionChange={handleRowAction}
                />
                {importRecord.status === "preview_ready" ? (
                  <div className="import-actions">
                    <button type="button" className="dealer-portal-btn primary" disabled={busy} onClick={runImport}>
                      Confirmar importación
                    </button>
                  </div>
                ) : null}
              </>
            ) : null}

            {["completed", "completed_with_errors"].includes(importRecord.status) ? (
              <div className="dealer-state-box">
                <Link className="dealer-portal-btn" to={`/admin/listings?status=needs_review&dealerId=${importRecord.dealerId}`}>
                  Ver listings en revisión
                </Link>
              </div>
            ) : null}
          </>
        ) : null}
      </main>
    </AdminPageLayout>
  );
}
